import { FC, useEffect, useState } from 'react';
import AppLayout from '../../components/layout/app-layout/app-layout';
import BtButton from '../bt-button/bt-button';
import { ServiceDownload } from '../../services/service-download';
import { NotificationManager } from "react-notifications";
import { useTranslation } from 'react-i18next';
import {ServiceTranslation} from '../../services/translationService';
import { ServiceProjects } from '../../services/service-projects';
import Modal from '../../components/shared/Modal/Modal';
import { ServiceWeeklyDigest } from '../../services/service-weekly-digest';
import appState from '../../state/AppStateContainer';
import Card from '../../components/shared/Card/card';
import serviceFileNodeCleanup from '../../services/service-file-node-cleanup';

const PageReports: FC = () => {
  const [t] = useTranslation('reports');

  const [loadingRecommendedSuppliers, setLoadingRecommendedSuppliers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loadingSupplierOrgs, setLoadingSupplierOrgs] = useState(false);
  const [loadingBidCards, setLoadingBidCards] = useState(false);
  const [loadingLiabilityInsurances, setLoadingLiabilityInsurances] = useState(false);
  const [loadingTranslationFilesStage, setLoadingTranslationFilesStage] = useState('');
  const [translationVersion, setTranslationVersion] = useState(-1);
  const [clearingAllNotifications, setClearingAllNotifications] = useState(false);
  const [showClearNotificationsModal, setShowClearNotificationsModal] = useState(false);
  const [showRecalculateModal, setShowRecalculateModal] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [runningFileNodeCleanup, setRunningFileNodeCleanup] = useState(false);

  const translationFileUpdated = t('button.translation.stage.updated', 'uppdaterad, kommer att finnas på alla servrar om en minut');

  useEffect(() => {
    (async () => {
      const translationVersion = await ServiceTranslation.getLatestVersionNumber();
      setTranslationVersion(translationVersion);
    })()
  }, []);

  const downloadReport = async (report: string, setLoading: (value: boolean) => void) => {
    setLoading(true);
    const now = new Date().toISOString().replace("T", "_").replace(/[.].*/, "");
    try {
      await ServiceDownload.downloadUrl(`/api/reports/${report}`, `${report}-${now}.csv`, "text/csv");
    } catch (e) {
      NotificationManager.error("" + e, t('notification.error', 'Något gick fel'));
    } finally {
      setLoading(false);
    }
  }
  const downloadRecommendedSuppliersReport = async () => {
    return await downloadReport("recommended-suppliers", setLoadingRecommendedSuppliers);
  }
  const downloadUsersReport = async () => {
    return await downloadReport("users", setLoadingUsers);
  }
  const downloadContractsReport = async () => {
    return await downloadReport("contracts", setLoadingContracts);
  }
  const downloadProjectsReport = async () => {
    return await downloadReport("projects", setLoadingProjects);
  }
  const downloadSuppliersReport = async () => {
    return await downloadReport("suppliers", setLoadingSuppliers);
  }
  const downloadSupplierOrgsReport = async () => {
    return await downloadReport("supplier-orgs", setLoadingSupplierOrgs);
  }
  const downloadBidCardsReport = async () => {
    return await downloadReport("bid-cards", setLoadingBidCards);
  }
  const downloadLiabilityInsurancesReport = async () => {
    return await downloadReport("liability-insurances", setLoadingLiabilityInsurances);
  }

  // Maximum 100 seconds + initial delay, should be enough to get the translation files. If not something went wrong.
  const getTranslationVersion = async (processId: string, retries = 100) => {
    if (retries <= 0) {
      const status = await ServiceTranslation.getProcessStatus(processId);
      setLoadingTranslationFilesStage(`status: ${status.status}, message: ${status.message}, ${t('button.translation.stage.error', 'för många försök att hämta översättningsfiler, för att få mer information använd processId')}: ${processId}`);
      return;
    }
    const newTranslationVersion = await ServiceTranslation.getLatestVersionNumber();

    if (newTranslationVersion !== translationVersion) {
      setTranslationVersion(newTranslationVersion);
      setLoadingTranslationFilesStage(translationFileUpdated);
      setTimeout(async () => {
        setLoadingTranslationFilesStage('');
      }, 5000);
      return;
    }

    setTimeout(async () => {
      await getTranslationVersion(processId, retries - 1);
    }, 1000);
  }

  const updateTranslationFiles = async () => {
    setLoadingTranslationFilesStage(t('button.translation.stage.queueing', 'i kö'));
    const processId = await ServiceTranslation.downloadTranslationFiles();
    setLoadingTranslationFilesStage(t('button.translation.stage.queued', 'i kö, väntar på att Lokalise ska behandla'));
    setTimeout(async () => {
      await getTranslationVersion(processId);
    }, 20000); // Initial polling delay, no need to poll too early.
  }

  const handleClearAllNotifications = async () => {
    setClearingAllNotifications(true);
    try {
      const result = await ServiceProjects.clearAllNotificationsFromAllOrganizations();
      
      if (result.count === 0) {
        NotificationManager.info(t('clearNotifications.noNotifications', 'Det finns inga aviseringar att rensa'));
      } else {
        NotificationManager.success(t('clearNotifications.success', 'Alla aviseringar har rensats'));
      }
      
      setShowClearNotificationsModal(false);
    } catch (e) {
      NotificationManager.error("" + e, t('notification.error', 'Något gick fel'));
    } finally {
      setClearingAllNotifications(false);
    }
  }

  const handleRecalculateWeeklyDigest = async () => {
    setRecalculating(true);
    try {
      await ServiceWeeklyDigest.recalculate();
      NotificationManager.success(t('weeklyDigest.recalculate.success', 'Weekly digest recalculated'));
      setShowRecalculateModal(false);
    } catch (e) {
      NotificationManager.error("" + e, t('notification.error', 'Något gick fel'));
    } finally {
      setRecalculating(false);
    }
  };

  const handleRunFileNodeCleanup = async () => {
    setRunningFileNodeCleanup(true);
    try {
      const data = await serviceFileNodeCleanup.runCleanup();
      NotificationManager.success(t('fileNodeCleanup.success', 'File nodes deleted: {{deletedFileNodes}}, Azure blobs deleted: {{blobsDeleted}}', {
        deletedFileNodes: data.deletedFileNodes,
        blobsDeleted: data.blobsDeleted,
      }));
    } catch (e: any) {
      NotificationManager.error(e.response?.data?.error ?? e.message ?? "" + e, t('notification.error', 'Något gick fel'));
    } finally {
      setRunningFileNodeCleanup(false);
    }
  };

  const handleSendWeeklyDigestToMyself = async () => {
    const userId = appState.getUserId();
    setSendingDigest(true);
    try {
      await ServiceWeeklyDigest.sendToUser(userId);
      NotificationManager.success(t('weeklyDigest.send.success', 'Weekly digest sent'));
    } catch (e) {
      NotificationManager.error("" + e, t('notification.error', 'Något gick fel'));
    } finally {
      setSendingDigest(false);
    }
  };

  const isNotSeparateInstance = window.location.hostname === 'localhost' || window.location.hostname === 'test.accurator.se' || window.location.hostname === 'app.accurator.se';

  return <AppLayout title={t('pageTitle', 'Rapporter')}>
    <Card title={t("card.reports", "Rapporter")} style={{margin: "16px 32px"}}>
      <div style={{display:"flex", gap: "16px", marginTop: "24px"}}>
        <BtButton onClick={downloadRecommendedSuppliersReport} loaderShow={loadingRecommendedSuppliers} color="dark">{t('button.recommendedSuppliers', 'Rekommenderade leverantörer')}</BtButton>
        <BtButton onClick={downloadUsersReport} loaderShow={loadingUsers} color="dark">{t('button.users', 'Användare')}</BtButton>
        <BtButton onClick={downloadContractsReport} loaderShow={loadingContracts} color="dark">{t('button.contracts', 'Kontrakt')}</BtButton>
        <BtButton onClick={downloadProjectsReport} loaderShow={loadingProjects} color="dark">{t('button.projects', 'Projekt')}</BtButton>
        <BtButton onClick={downloadSuppliersReport} loaderShow={loadingSuppliers} color="dark">{t('button.suppliers', 'Leverantör')}</BtButton>
        <BtButton onClick={downloadSupplierOrgsReport} loaderShow={loadingSupplierOrgs} color="dark">{t('button.supplierOrgs', 'Leverantörsorganisationer')}</BtButton>
        <BtButton onClick={downloadBidCardsReport} loaderShow={loadingBidCards} color="dark">{t('button.bidCards', 'Upphandlingar')}</BtButton>
        <BtButton onClick={downloadLiabilityInsurancesReport} loaderShow={loadingLiabilityInsurances} color="dark">{t('button.liabilityInsurances', 'Ansvarsförsäkringar')}</BtButton>
      </div>
    </Card>

    {isNotSeparateInstance && (
      <Card title={t("card.translations", "Översättningsfiler")} style={{margin: "16px 32px"}}>
        <div style={{marginTop:"24px", display: 'flex', gap: '16px', alignItems: 'center'}}>
          <BtButton onClick={updateTranslationFiles} loaderShow={loadingTranslationFilesStage !== '' && loadingTranslationFilesStage !== translationFileUpdated} color="dark">{t('button.translation.text', 'Uppdatera översättningsfile')}</BtButton>
          <span style={{ fontSize: '14px' }}>{t('button.translation.currentVersion', 'Nuvarande version:')} {translationVersion}</span>
          {loadingTranslationFilesStage !== '' && <span style={{ fontSize: '14px' }}>| {loadingTranslationFilesStage}</span>}
        </div>
      </Card>
    )}

    <Card title={t("card.notifications", "Aviseringar")} style={{margin: "16px 32px"}}>
      <div style={{display:"flex", gap: "16px", marginTop: "24px"}}>
        <BtButton onClick={() => setShowClearNotificationsModal(true)} color="dark">{t('button.clearNotifications', 'Rensa alla aviseringar')}</BtButton>
      </div>
    </Card>

    <Card title={t("card.weeklyDigest", "Veckouppdateringar")} style={{margin: "16px 32px"}}>
      <div style={{display:"flex", gap: "16px", marginTop: "24px", alignItems: 'center'}}>
        <BtButton onClick={() => setShowRecalculateModal(true)} color="dark">{t('button.recalculateWeeklyDigest', 'Recalculate weekly digest')}</BtButton>
        <BtButton onClick={handleSendWeeklyDigestToMyself} loaderShow={sendingDigest} color="dark">{t('button.sendWeeklyDigestToYourself', 'Send weekly to myself')}</BtButton>
      </div>
    </Card>

    <Card title={t("card.fileNodeCleanup", "File Node Cleanup")} style={{margin: "16px 32px"}}>
      <p style={{ marginTop: 0 }}>{t('fileNodeCleanup.description', 'Deletes expired file node records and removes their associated Azure blobs where applicable. This runs automatically every night at 2 AM but can be triggered manually here for testing.')}</p>
      <div style={{display:"flex", gap: "16px", marginTop: "24px"}}>
        <BtButton onClick={handleRunFileNodeCleanup} loaderShow={runningFileNodeCleanup} color="dark">{t('button.runFileNodeCleanup', 'Run cleanup now')}</BtButton>
      </div>
    </Card>

    <Modal
      title={t('weeklyDigest.recalculate.modal.title', 'Recalculate weekly digest')}
      show={showRecalculateModal}
      setShow={setShowRecalculateModal}
      buttonInfo={{
        label: t('weeklyDigest.recalculate.modal.holdButton', 'Hold to confirm'),
        action: handleRecalculateWeeklyDigest,
        loaderShow: recalculating,
        holdToConfirm: true,
        color: 'dark',
      }}
    >
      <p>{t('weeklyDigest.recalculate.modal.message', 'This will delete the current weekly digest and generate a new one.')}</p>
    </Modal>

    <Modal
      title={t('clearNotifications.modal.title', 'Rensa alla aviseringar')}
      show={showClearNotificationsModal}
      setShow={setShowClearNotificationsModal}
      buttonInfo={{
        label: t('clearNotifications.modal.confirm', 'Rensa'),
        loaderShow: clearingAllNotifications,
        action: handleClearAllNotifications,
      }}
    >
      <span>{t('clearNotifications.modal.message', 'Är du säker på att du vill rensa alla aviseringar från alla projekt? Detta kan inte ångras.')}</span>
    </Modal>
  </AppLayout>
}

export default PageReports;
